import { useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CUSTOMER_AREAS, FEATURE_FLAGS, areaName, isInternalArea } from '@/lib/productAreas';
import { cn } from '@/lib/utils';
import type { EvidenceKind, GuideOption, ReleaseNoteAudience, ReleaseNoteType } from '@/types/releaseNotes';
import { TYPE_LABELS } from '../format';
import { TypeTag } from '../EditionArticle';
import { BODY_MAX, TITLE_MAX, type DraftItem } from './editorState';

export const selectClass =
  'h-[var(--etn-control-height-md)] w-full rounded-etn-pill border border-etn-border-input bg-etn-surface px-3 text-etn-control text-etn-text-cell';

const EVIDENCE_LABELS: Record<EvidenceKind, string> = {
  'customer-request': 'Customer request in Linear',
  'ticket-link': 'Linked support conversation',
  described: 'Described in the issue'
};

function Counter({ value, max }: { value: string; max: number }) {
  return (
    <span className={cn('text-etn-micro font-semibold tabular-nums', value.length > max ? 'text-etn-danger-text' : 'text-etn-text-soft')}>
      {value.length}/{max}
    </span>
  );
}

function Provenance({ item, restoredSummary }: { item: DraftItem; restoredSummary?: string }) {
  const { prs, linear } = item.sources;
  const evidence = item.evidence;
  const weak = item.type === 'fixed' && evidence?.kind === 'described';
  const missing = item.type === 'fixed' && !evidence?.kind;
  if (!prs.length && !linear.length && !evidence && !restoredSummary && !missing) return null;
  return (
    // Only a note that needs a second look gets the tinted box; otherwise its sources are a quiet line.
    <div
      className={cn(
        'space-y-1.5',
        weak || missing || restoredSummary
          ? 'rounded-etn-menu bg-etn-surface-sunken px-3 py-2.5 text-etn-cell text-etn-text-cell'
          : 'text-etn-micro text-etn-text-label'
      )}
    >
      {restoredSummary && (
        <p>
          <span className="font-semibold text-etn-text-label">Restored: </span>
          {restoredSummary}
        </p>
      )}
      {(prs.length > 0 || linear.length > 0) && (
        <p className="flex flex-wrap gap-x-3 gap-y-1">
          <span className="font-semibold text-etn-text-label">From</span>
          {prs.map(pr => (
            <a
              key={`${pr.repo}#${pr.number}`}
              href={`https://github.com/etainabl/${pr.repo}/pull/${pr.number}`}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {pr.repo === 'platform' ? '' : pr.repo}#{pr.number}
            </a>
          ))}
          {linear.map(id => (
            <a key={id} href={`https://linear.app/etainabl/issue/${id}`} target="_blank" rel="noreferrer" className="underline">
              {id}
            </a>
          ))}
        </p>
      )}
      {item.type === 'fixed' && (
        <p className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-etn-text-label">Reported by a customer</span>
          {evidence?.kind ? <span>{EVIDENCE_LABELS[evidence.kind]}</span> : <span>No evidence recorded</span>}
          {(weak || missing) && (
            <Badge variant="warning" data-testid="evidence-warning">
              Check this was customer-reported
            </Badge>
          )}
          {evidence?.note && <q className="basis-full text-etn-text-label">{evidence.note}</q>}
        </p>
      )}
    </div>
  );
}

/** The note as a reviewer scans it: what readers will see, who sees it, and anything to check. */
function ItemSummary({ item, unknownFlags, open, onToggle }: { item: DraftItem; unknownFlags: string[]; open: boolean; onToggle: () => void }) {
  const flagLabels = item.featureFlags.map(code => FEATURE_FLAGS.find(f => f.code === code)?.label ?? code);
  return (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start gap-2.5">
          <TypeTag type={item.type} />
          <p className={cn('text-sm font-semibold leading-snug', item.title ? 'text-etn-text' : 'text-etn-text-soft')}>
            {item.title || 'Untitled note'}
          </p>
        </div>
        {item.body && !open && <p className="line-clamp-2 max-w-[75ch] text-etn-cell text-etn-text-cell">{item.body}</p>}
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-etn-micro font-semibold text-etn-text-label">
          <span className={cn(isInternalArea(item.area) && 'text-etn-danger-text')}>{areaName(item.area)}</span>
          {item.audience === 'partner' && <span>· Partner admins</span>}
          {flagLabels.length > 0 && <span>· Only with {flagLabels.join(', ')}</span>}
          {item.guideSlug && <span>· Links a guide</span>}
          {unknownFlags.length > 0 && <Badge variant="destructive">Unknown flag</Badge>}
        </p>
      </div>
      <Button type="button" size="sm" variant={open ? 'secondary' : 'outline'} onClick={onToggle} aria-expanded={open}>
        {open ? <ChevronDown /> : <Pencil />}
        {open ? 'Done' : 'Edit'}
      </Button>
    </div>
  );
}

export function ItemEditor({
  item,
  index,
  count,
  guides,
  restoredSummary,
  defaultOpen,
  onChange,
  onMove,
  onRemove
}: {
  item: DraftItem;
  index: number;
  count: number;
  guides: GuideOption[];
  restoredSummary?: string;
  defaultOpen: boolean;
  onChange: (patch: Partial<DraftItem>) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const id = (field: string) => `item-${item.key}-${field}`;
  const knownFlags = new Set(FEATURE_FLAGS.map(f => f.code));
  const unknownFlags = item.featureFlags.filter(code => !knownFlags.has(code));
  const areaGuides = guides.filter(g => g.area === item.area);
  const otherGuides = guides.filter(g => g.area !== item.area);
  const toggleFlag = (code: string) =>
    onChange({ featureFlags: item.featureFlags.includes(code) ? item.featureFlags.filter(f => f !== code) : [...item.featureFlags, code] });

  return (
    <li className="space-y-3 py-5 first:pt-0" data-testid="release-note-item-editor">
      <div className="flex items-start gap-3">
        <span className="mt-2 w-6 shrink-0 text-right text-etn-micro font-semibold tabular-nums text-etn-text-soft">{index + 1}</span>
        <div className="min-w-0 flex-1 space-y-3">
          <ItemSummary item={item} unknownFlags={unknownFlags} open={open} onToggle={() => setOpen(o => !o)} />
          {open && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor={id('title')}>Title</Label>
                  <Counter value={item.title} max={TITLE_MAX} />
                </div>
                <Input
                  id={id('title')}
                  value={item.title}
                  onChange={e => onChange({ title: e.target.value })}
                  placeholder="What the user can now do"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor={id('body')}>Text</Label>
                  <Counter value={item.body} max={BODY_MAX} />
                </div>
                <Textarea
                  id={id('body')}
                  rows={2}
                  value={item.body}
                  onChange={e => onChange({ body: e.target.value })}
                  placeholder="One or two plain sentences."
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor={id('area')}>Area</Label>
                  <select
                    id={id('area')}
                    className={selectClass}
                    value={item.area}
                    onChange={e => onChange({ area: e.target.value, guideSlug: null })}
                  >
                    {isInternalArea(item.area) && <option value={item.area}>{areaName(item.area)} (not for customers)</option>}
                    {CUSTOMER_AREAS.map(area => (
                      <option key={area.key} value={area.key}>
                        {area.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={id('type')}>Type</Label>
                  <select
                    id={id('type')}
                    className={selectClass}
                    value={item.type}
                    onChange={e => onChange({ type: e.target.value as ReleaseNoteType })}
                  >
                    {(Object.keys(TYPE_LABELS) as ReleaseNoteType[]).map(type => (
                      <option key={type} value={type}>
                        {TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={id('audience')}>Audience</Label>
                  <select
                    id={id('audience')}
                    className={selectClass}
                    value={item.audience}
                    onChange={e => onChange({ audience: e.target.value as ReleaseNoteAudience })}
                  >
                    <option value="everyone">Everyone</option>
                    <option value="partner">Partner admins</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={id('guide')}>Guide</Label>
                <select
                  id={id('guide')}
                  className={selectClass}
                  value={item.guideSlug ?? ''}
                  onChange={e => onChange({ guideSlug: e.target.value || null })}
                >
                  <option value="">No guide</option>
                  {item.guideSlug && !guides.some(g => g.slug === item.guideSlug) && (
                    <option value={item.guideSlug}>{item.guideSlug} (not published)</option>
                  )}
                  {areaGuides.length > 0 && (
                    <optgroup label={areaName(item.area)}>
                      {areaGuides.map(g => (
                        <option key={g.slug} value={g.slug}>
                          {g.title}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {otherGuides.length > 0 && (
                    <optgroup label="Other guides">
                      {otherGuides.map(g => (
                        <option key={g.slug} value={g.slug}>
                          {g.title}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <fieldset className="space-y-1.5">
                <legend className="text-sm font-semibold">Only for companies with</legend>
                <div className="flex flex-wrap gap-1.5">
                  {[...FEATURE_FLAGS, ...unknownFlags.map(code => ({ code, label: code }))].map(flag => {
                    const on = item.featureFlags.includes(flag.code);
                    const unknown = !knownFlags.has(flag.code);
                    return (
                      <button
                        key={flag.code}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleFlag(flag.code)}
                        className={cn(
                          'h-7 rounded-etn-pill border px-3 text-etn-micro font-semibold transition-colors',
                          on
                            ? 'border-transparent bg-etn-accent-tint text-etn-accent-text-active'
                            : 'border-etn-border-input bg-etn-surface text-etn-text-label hover:bg-etn-control-subtle',
                          unknown && 'border-transparent bg-etn-danger-subtle text-etn-danger-text'
                        )}
                        title={unknown ? 'Not a known feature flag: remove it before approving' : undefined}
                      >
                        {flag.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-etn-micro text-etn-text-soft">None selected means everyone with access to the area sees it.</p>
              </fieldset>
            </div>
          )}
          <Provenance item={item} restoredSummary={restoredSummary} />
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          <Button type="button" size="icon-sm" variant="ghost" aria-label="Move up" disabled={index === 0} onClick={() => onMove(-1)}>
            <ArrowUp />
          </Button>
          <Button type="button" size="icon-sm" variant="ghost" aria-label="Move down" disabled={index === count - 1} onClick={() => onMove(1)}>
            <ArrowDown />
          </Button>
          <Button type="button" size="icon-sm" variant="ghost" aria-label="Remove note" onClick={onRemove}>
            <Trash2 className="text-etn-danger-text" />
          </Button>
        </div>
      </div>
    </li>
  );
}
